import React, { useState, useRef, useEffect } from 'react';
import { Box, Flex, ActionIcon, Text, Avatar, Button, TextInput, Stack, Modal, Drawer, ScrollArea, Badge, Popover, SimpleGrid, AspectRatio, Group } from '@mantine/core';
import { IconHeart, IconMessageCircle, IconShoppingCart, IconLock, IconCloudDownload, IconShare, IconRobot, IconSend, IconFilter, IconChevronUp, IconChevronDown, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.ailinkb.shop';

export default function MarketplaceFeed({ addToCart, user }) {
  const { t } = useTranslation();
  const [chatOpen, setChatOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedVideoIndex, setSelectedVideoIndex] = React.useState(null);
  const [activeExpandedIndex, setActiveExpandedIndex] = React.useState(0);
  const scrollContainerRef = React.useRef(null);

  const [skillChatOpen, setSkillChatOpen] = React.useState(false);
  const [activeSkillChat, setActiveSkillChat] = React.useState(null);
  const [skillChatMessages, setSkillChatMessages] = React.useState([]);
  const [skillChatInput, setSkillChatInput] = React.useState('');
  const [skillChatLoading, setSkillChatLoading] = React.useState(false);
  const chatScrollRef = React.useRef(null);
  
  React.useEffect(() => {
    if (chatScrollRef.current) {
        chatScrollRef.current.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [skillChatMessages]);
  
  const fetchSkillChatHistory = async (sessionId) => {
    try {
      const res = await fetch(import.meta.env.VITE_API_BASE_URL + '/api/chat/history/' + sessionId);
      if (res.ok) {
        const data = await res.json();
        setSkillChatMessages(data);
        if (data.length > 0 && data[data.length - 1].role === 'SYSTEM' && data[data.length - 1].content === 'AI processing started') {
          setTimeout(() => fetchSkillChatHistory(sessionId), 2000);
        } else {
          setSkillChatLoading(false);
        }
      }
    } catch (e) {
      console.error(e);
      setSkillChatLoading(false);
    }
  };

  const handleSendSkillMessage = async () => {
    if (!skillChatInput.trim() || !activeSkillChat) return;
    const msg = skillChatInput;
    setSkillChatInput('');
    const sessionId = 'skill_chat_' + (user?.email || 'anon') + '_' + activeSkillChat.id;
    setSkillChatMessages(prev => [...prev, { role: 'USER', content: msg }]);
    setSkillChatLoading(true);
    
    try {
        await fetch(import.meta.env.VITE_API_BASE_URL + '/api/chat/reply', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                sessionId: sessionId,
                userMessage: msg,
                ownerEmail: activeSkillChat.ownerEmail,
                visitorEmail: user?.email || 'anonymous',
                templateKey: 'skill_' + activeSkillChat.id,
                language: 'Polski'
            })
        });
        setTimeout(() => fetchSkillChatHistory(sessionId), 1500);
    } catch (e) {
        console.error(e);
        setSkillChatLoading(false);
    }
  };

  
  React.useEffect(() => {
    if (selectedVideoIndex !== null && scrollContainerRef.current) {
      setActiveExpandedIndex(selectedVideoIndex);
      scrollContainerRef.current.scrollTop = selectedVideoIndex * window.innerHeight;
    }
  }, [selectedVideoIndex]);
  
  const [dbVideos, setDbVideos] = React.useState([]);

  React.useEffect(() => {
    fetch(`${API_BASE_URL}/api/skills/approved`)
      .then(res => res.json())
      .then(data => {
        const mapped = data.map(skill => ({
          id: skill.id,
          url: skill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${skill.videoUrl}` : skill.videoUrl,
          author: skill.ownerEmail.split('@')[0],
          ownerEmail: skill.ownerEmail,
          title: skill.title,
          description: skill.title + ' - ' + skill.description,
          likes: Math.floor(Math.random() * 500) + 10,
          comments: Math.floor(Math.random() * 50) + 1,
          price: skill.price,
          packageUrl: skill.packageUrl,
          tags: skill.tags ? skill.tags.split(/[,#]+/).map(t => t.trim()).filter(Boolean).map(t => '#' + t) : []
        }));
        setDbVideos(mapped);
      })
      .catch(err => console.error('Error fetching skills:', err));
  }, []);

  const bgVideos = dbVideos;

  const allTags = React.useMemo(() => {
    const tags = new Set();
    dbVideos.forEach(v => {
      if (v.tags) v.tags.forEach(t => tags.add(t));
    });
    return Array.from(tags);
  }, [dbVideos]);

  const performSemanticSearch = (overrideQuery) => {
    const q = typeof overrideQuery === 'string' ? overrideQuery : searchQuery;
    document.activeElement?.blur();
    let url = `${API_BASE_URL}/api/skills/search?query=${encodeURIComponent(q)}`;
    if (!q.trim()) url = `${API_BASE_URL}/api/skills/approved`;
    
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data)) {
            console.error('API did not return an array:', data);
            return;
        }
        const mapped = data.map(skill => ({
          id: skill.id,
          url: skill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${skill.videoUrl}` : skill.videoUrl,
          author: skill.ownerEmail.split('@')[0],
          ownerEmail: skill.ownerEmail,
          title: skill.title,
          description: skill.title + ' - ' + skill.description,
          likes: Math.floor(Math.random() * 500) + 10,
          comments: Math.floor(Math.random() * 50) + 1,
          price: skill.price,
          packageUrl: skill.packageUrl,
          tags: skill.tags ? skill.tags.split(/[,#]+/).map(t => t.trim()).filter(Boolean).map(t => '#' + t) : []
        }));
        setDbVideos(mapped);
      })
      .catch(err => console.error('Error fetching skills:', err));
  };

  return (
    <Box style={{ width: '100%', minHeight: '100%', backgroundColor: '#000', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      
      {/* Sekcja Górna - Wyszukiwarka */}
      <Box style={{ width: '100%', padding: '60px 20px 40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Text c="white" fw={900} size="3rem" ta="center" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)', marginBottom: '30px' }}>
          {t('hero_title_discover')} <br/>
          <Text span variant="gradient" gradient={{ from: '#ff4b1f', to: '#ff9068', deg: 45 }}>{t('hero_title_skills')}</Text>
        </Text>
        <Flex 
          align="center"
          style={{ width: '100%', maxWidth: 600, backgroundColor: 'rgba(20, 20, 20, 0.7)', backdropFilter: 'blur(15px)', borderRadius: 30, padding: '10px 15px 10px 25px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
        >
          <IconRobot color="#fff" size={28} style={{ marginRight: 15 }} />
          <TextInput 
            placeholder={t('marketplace_search_placeholder')}
            variant="unstyled"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1 }}
            styles={{ input: { color: '#fff', fontSize: '1.2rem' } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                performSemanticSearch();
              }
            }}
          />
          {searchQuery && (
            <ActionIcon radius="xl" size="xl" variant="subtle" color="gray" onClick={() => { setSearchQuery(''); performSemanticSearch(''); }} style={{ marginRight: 10 }}>
              <IconX size={24} color="#fff" />
            </ActionIcon>
          )}
          <ActionIcon radius="xl" size="xl" variant="subtle" color="gray" onClick={() => setChatOpen(true)} style={{ marginRight: 10 }}>
            <IconFilter size={24} color="#fff" />
          </ActionIcon>
          <ActionIcon radius="xl" size="xl" color="indigo" variant="filled" onClick={performSemanticSearch}>
            <IconSend size={24} />
          </ActionIcon>
        </Flex>
      </Box>

      {/* Siatka Wideo na pierwszym planie */}
      <Box style={{ flex: 1, padding: '0 20px 40px 20px', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="lg">
          {dbVideos.map((video, idx) => (
             <AspectRatio ratio={9/16} key={'tile-'+idx}>
               <VideoTile video={video} addToCart={addToCart} isExpanded={false} onExpand={() => setSelectedVideoIndex(idx)} user={user} openSkillChat={(v) => { setActiveSkillChat(v); setSkillChatOpen(true); fetchSkillChatHistory('skill_chat_' + (user?.email || 'anon') + '_' + v.id); }} />
             </AspectRatio>
          ))}
        </SimpleGrid>
      </Box>

      {/* Modal / Pełny ekran do oglądania feedu (TikTok style) */}
      <Modal
        opened={selectedVideoIndex !== null}
        onClose={() => setSelectedVideoIndex(null)}
        fullScreen
        withCloseButton={false}
        styles={{ content: { backgroundColor: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(10px)' }, inner: { padding: 0 } }}
      >
        {selectedVideoIndex !== null && (
          <Box 
            ref={scrollContainerRef}
            style={{ width: '100vw', height: '100vh', overflowY: 'scroll', scrollSnapType: 'y mandatory' }}
            onScroll={(e) => {
              const st = e.target.scrollTop;
              const vh = window.innerHeight;
              const activeIdx = Math.round(st / vh);
              if (activeIdx !== activeExpandedIndex) {
                setActiveExpandedIndex(activeIdx);
              }
            }}
          >
            <ActionIcon 
              color="white" 
              size="xl" 
              radius="xl" 
              variant="transparent" 
              style={{ position: 'fixed', top: 20, right: 20, zIndex: 100 }}
              onClick={() => setSelectedVideoIndex(null)}
            >
              <Text size="2rem">✖</Text>
            </ActionIcon>

            {/* Navigation Arrows for UX */}
            <Flex direction="column" gap="md" style={{ position: 'fixed', right: 20, top: '50%', transform: 'translateY(-50%)', zIndex: 100 }}>
              <ActionIcon 
                radius="xl" 
                size="xl" 
                variant="filled" 
                color="dark"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)' }}
                disabled={activeExpandedIndex === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (activeExpandedIndex > 0 && scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTo({ top: (activeExpandedIndex - 1) * window.innerHeight, behavior: 'smooth' });
                  }
                }}
              >
                <IconChevronUp size={28} color="white" />
              </ActionIcon>
              
              <ActionIcon 
                radius="xl" 
                size="xl" 
                variant="filled" 
                color="dark"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)' }}
                disabled={activeExpandedIndex === bgVideos.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  if (activeExpandedIndex < bgVideos.length - 1 && scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTo({ top: (activeExpandedIndex + 1) * window.innerHeight, behavior: 'smooth' });
                  }
                }}
              >
                <IconChevronDown size={28} color="white" />
              </ActionIcon>
            </Flex>

            {bgVideos.map((video, idx) => (
              <Box 
                key={'feed-'+idx}
                style={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', scrollSnapAlign: 'start', position: 'relative' }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedVideoIndex(null);
                  }
                }}
              >
                <Box style={{ height: '90vh', aspectRatio: '9/16', position: 'relative', borderRadius: 20, overflow: 'hidden', boxShadow: '0 0 50px rgba(99,102,241,0.5)' }}>
                  <VideoTile video={video} addToCart={addToCart} isExpanded={idx === activeExpandedIndex} onExpand={() => setSelectedVideoIndex(null)} user={user} openSkillChat={(v) => { setActiveSkillChat(v); setSkillChatOpen(true); fetchSkillChatHistory('skill_chat_' + (user?.email || 'anon') + '_' + v.id); }} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Modal>

      {/* Drawer z Czatem AI */}
      <Drawer opened={chatOpen} onClose={() => setChatOpen(false)} position="right" size="md" title={<Flex align="center"><IconFilter size={24} color="#6366f1" style={{ marginRight: 10 }}/><Text fw={700}>{t("filter_tags_title", "Filtruj Tagami")}</Text></Flex>} styles={{ content: { backgroundColor: '#1a1b1e', color: '#fff' }, header: { backgroundColor: '#1a1b1e', color: '#fff' }, close: { color: '#fff' } }}>
        <Box p="md">
          <Text size="sm" c="dimmed" mb="xl">{t("filter_tags_desc", "Wybierz technologie i modele AI, których szukasz w bazie:")}</Text>
          <Flex wrap="wrap" gap="sm">
            {allTags.map(tag => {
              const isActive = searchQuery.includes(tag);
              return (
                <Badge 
                  key={tag} 
                  variant={isActive ? "filled" : "outline"} 
                  color={isActive ? "indigo" : "gray"} 
                  size="xl" 
                  radius="md"
                  style={{ 
                    cursor: 'pointer', 
                    backgroundColor: isActive ? '#4f46e5' : 'transparent', 
                    color: '#fff', 
                    borderColor: isActive ? 'transparent' : '#555',
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '10px 15px',
                    height: 'auto'
                  }}
                  onClick={() => {
                    if (isActive) {
                      setSearchQuery(searchQuery.replace(tag, '').trim());
                    } else {
                      setSearchQuery((searchQuery + ' ' + tag).trim());
                    }
                  }}
                >
                  {tag}
                </Badge>
              )
            })}
          </Flex>
          <Button 
            fullWidth 
            mt="xl" 
            size="lg" 
            color="indigo" 
            variant="filled" 
            onClick={() => setChatOpen(false)}
          >
            {t("apply_filters_btn", "Zastosuj Filtry")}
          </Button>
        </Box>
      </Drawer>

      {/* Skill Chat Drawer */}
      <Drawer opened={skillChatOpen} onClose={() => setSkillChatOpen(false)} position="right" size="md" title={<Text fw={700}>Chat: {activeSkillChat?.title}</Text>} styles={{ content: { backgroundColor: '#1a1b1e', color: '#fff' }, header: { backgroundColor: '#1a1b1e', color: '#fff' }, close: { color: '#fff' } }}>
        <Box p="md" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
          <Text size="sm" c="dimmed" mb="md">Porozmawiaj z tym promptem jako serwis (PaaS).</Text>
          <ScrollArea style={{ flex: 1 }} viewportRef={chatScrollRef} mb="md">
            <Stack gap="sm">
              {skillChatMessages.filter(m => m.role !== 'SYSTEM').map((msg, idx) => (
                <Box key={idx} p="sm" style={{ borderRadius: 12, backgroundColor: msg.role === 'USER' ? '#4f46e5' : '#2C2E33', alignSelf: msg.role === 'USER' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                  <Text size="sm" c="white">{msg.content}</Text>
                </Box>
              ))}
              {skillChatLoading && <Text size="xs" c="dimmed" fs="italic">AI analizuje...</Text>}
            </Stack>
          </ScrollArea>
          <Group align="flex-end">
            <TextInput style={{ flex: 1 }} placeholder="Napisz do prompta..." value={skillChatInput} onChange={e => setSkillChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSendSkillMessage(); }} />
            <Button color="indigo" onClick={handleSendSkillMessage} loading={skillChatLoading}><IconSend size={18} /></Button>
          </Group>
        </Box>
      </Drawer>

    </Box>
  );
}

// Pojedynczy kafelek w siatce
function VideoTile({ video, addToCart, onExpand, isExpanded = false, user, openSkillChat }) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = React.useState(isExpanded);
  
  React.useEffect(() => { 
    setIsPlaying(isExpanded); 
  }, [isExpanded]);
  
  const [isHovered, setIsHovered] = React.useState(false);
  const videoRef = React.useRef(null);
  const iframeRef = React.useRef(null);

  let thumbnailUrl = '';
  const isYouTube = video.url && (video.url.includes('youtube.com') || video.url.includes('youtu.be'));
  if (isYouTube) {
    const match = video.url.match(/(?:youtu\.be\/|v=\/|v=)([\w-]+)/);
    if (match && match[1]) {
      thumbnailUrl = `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
  }

  // Effect for explicit play of local video
  React.useEffect(() => {
    if (isPlaying && !isYouTube && videoRef.current) {
      videoRef.current.play().catch(e => console.log("Autoplay prevented:", e));
    } else if (!isPlaying && !isYouTube && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isPlaying, isYouTube]);

  return (
    <Box 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        height: '100%', 
        width: '100%', 
        position: 'relative', 
        borderRadius: 16, 
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: isHovered ? '0 10px 30px rgba(99, 102, 241, 0.4)' : 'none',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-5px)' : 'none',
        backgroundColor: '#1a1b1e'
      }}
      onClick={() => {
        const isImage = video.url?.toLowerCase().endsWith('.jpg') || video.url?.toLowerCase().endsWith('.jpeg') || video.url?.toLowerCase().endsWith('.png');
        if (isImage) {
          onExpand();
        } else if (!isPlaying) {
          setIsPlaying(true);
        }
      }}
    >

      {/* Price Badge (Top Left) */}
      <Box style={{ position: 'absolute', top: 12, left: 12, zIndex: 10, pointerEvents: 'none' }}>
        {video.price === 0 ? (
          <Badge color="green" size="lg" variant="filled" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.6)' }}>{t('free_prompt_badge')}</Badge>
        ) : (
          <Badge color="blue" size="lg" variant="filled" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.6)' }}>
            {video.price} EUR
          </Badge>
        )}
      </Box>

      {/* 1. YOUTUBE VIDEO */}
      {isYouTube && (
        <>
          {!isPlaying ? (
            <img src={thumbnailUrl} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.35)' }} />
          ) : (
            <iframe
              ref={iframeRef}
              src={video.url.replace('watch?v=', 'embed/').replace('shorts/', 'embed/') + '?autoplay=1&mute=0&controls=1'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{ width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
            />
          )}
        </>
      )}

      {/* 2. LOCAL VIDEO OR IMAGE */}
      {!isYouTube && (
        (video.url?.toLowerCase().endsWith('.jpg') || video.url?.toLowerCase().endsWith('.jpeg') || video.url?.toLowerCase().endsWith('.png')) ? (
          <img 
            src={video.url}
            alt={video.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <video 
            ref={videoRef}
            src={video.url} 
            preload="metadata"
            controls={isPlaying}
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )
      )}

      {/* Play Icon (Visible only when not playing AND is a video) */}
      {!isPlaying && !(video.url?.toLowerCase().endsWith('.jpg') || video.url?.toLowerCase().endsWith('.jpeg') || video.url?.toLowerCase().endsWith('.png')) && (
        <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 5, pointerEvents: 'none' }}>
          <ActionIcon radius="xl" size={60} color="indigo" variant="filled" style={{ opacity: 0.9 }}>
            <Text size="2rem" style={{ marginLeft: 5 }}>▶</Text>
          </ActionIcon>
        </Box>
      )}

      {/* Expand Icon (Visible only when playing) */}
      {isPlaying && (
        <ActionIcon 
          color="white" 
          variant="filled" 
          size="md" 
          radius="xl"
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { e.stopPropagation(); onExpand(); }}
        >
          <Text size="sm">⛶</Text>
        </ActionIcon>
      )}

      {/* Overlays (Hidden when playing) */}
      <Box style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(transparent, rgba(0,0,0,0.9))', pointerEvents: 'none', opacity: isPlaying ? 0 : 1, transition: 'opacity 0.3s' }} />

      <Box style={{ position: 'absolute', bottom: 15, left: 15, right: 60, color: '#fff', pointerEvents: 'none', opacity: isPlaying ? 0 : 1, transition: 'opacity 0.3s' }}>
        <Text size="md" fw={700}>@{video.author}</Text>
        <Text size="xs" mt={5} lineClamp={2}>{video.description}</Text>
      </Box>

      <Box style={{ position: 'absolute', bottom: 15, right: 15, opacity: isPlaying ? 0 : 1, transition: 'opacity 0.3s' }}>
        <Flex direction="column" align="center">
          {video.price === 0 ? (
            user?.subscriptionStatus === 'ACTIVE' ? (
              <Group gap="xs" wrap="nowrap" style={{ pointerEvents: 'auto' }}>
                <ActionIcon 
                  radius="xl" 
                  size="lg" 
                  color="grape" 
                  variant="filled" 
                  style={{ width: 40, height: 40, boxShadow: '0 0 10px rgba(192, 132, 252, 0.5)' }}
                  onClick={(e) => { e.stopPropagation(); if (openSkillChat) openSkillChat(video); }}
                >
                  <IconMessageCircle size={20} />
                </ActionIcon>
                <ActionIcon 
                  radius="xl" 
                  size="lg" 
                  color="green" 
                  variant="filled" 
                  style={{ width: 40, height: 40, boxShadow: '0 0 10px rgba(74, 222, 128, 0.5)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (video.packageUrl) {
                      window.open(video.packageUrl.startsWith('/') ? import.meta.env.VITE_API_BASE_URL + video.packageUrl : video.packageUrl, '_blank');
                    } else {
                      alert(t('free_prompt_no_attachment', 'Ten darmowy prompt nie posiada załącznika.'));
                    }
                  }}
                >
                  <IconCloudDownload size={20} />
                </ActionIcon>
              </Group>
            ) : (
              <ActionIcon 
                radius="xl" 
                size="lg" 
                color="gray" 
                variant="filled" 
                style={{ width: 40, height: 40, boxShadow: '0 0 10px rgba(156, 163, 175, 0.5)', pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  alert(t('free_prompt_locked_alert'));
                }}
              >
                <IconLock size={20} />
              </ActionIcon>
            )
          ) : (
            <ActionIcon 
              radius="xl" 
              size="lg" 
              color="indigo" 
              variant="filled" 
              style={{ width: 40, height: 40, boxShadow: '0 0 10px rgba(99, 102, 241, 0.5)', pointerEvents: 'auto' }}
              onClick={(e) => {
                e.stopPropagation();
                if (addToCart) {
                  addToCart({
                    skillId: video.id,
                    serviceName: video.description ? video.description.substring(0, 30) + '...' : 'AI Skill',
                    ownerEmail: video.author,
                    amount: video.price,
                    currency: 'EUR'
                  });
                }
              }}
            >
              <IconShoppingCart size={20} />
            </ActionIcon>
          )}
          <Text size="10px" c="white" fw={800} mt={5}>{video.price === 0 ? t('free_prompt_badge') : video.price + ' EUR'}</Text>
        </Flex>
      </Box>
    </Box>
  );
}
